import axios from "axios"
import { Credentials } from "./credentials"
import {
  DateFilterOptions,
  FetchBrands,
  FetchCategories,
  FetchNotifications,
  FetchSegments,
  FetchTags,
  Filter,
} from "./EnvironmentContext"
import { ErrorCode } from "./errors"
import { toISODate, addDays, startOfDayDisplayed, toCustomISODate } from "./dates"

class SystemError extends Error {
  constructor(public errorCode: ErrorCode) {
    super()
  }
}

export const getBrands: FetchBrands = async (credentials) => get("/api/brands", credentials)

export const getNotifications: FetchNotifications = async (credentials, brand, filter, offset = 0, pageSize = 10) => {
  const ns: any[] = await get("/api/notifications", credentials, {
    brand,
    ...transformFilter(filter),
    offset,
    "page-size": pageSize,
  })
  return ns.map((n) => ({ ...n, date: new Date(n.date) }))
}

export const getTags: FetchTags = async (credentials, brand) => get("/api/tags", credentials, { brand })

export const getCategories: FetchCategories = async (credentials, brand) =>
  get("/api/categories", credentials, { brand })

export const getSegments: FetchSegments = async (credentials, brand) => get("/api/segments", credentials, { brand })

const toDateRange = (dateFilter: DateFilterOptions) => {
  const now = new Date(Date.now())
  const pastDate = new Date("January 01, 1900");
  const futureDate = new Date("January 01, 2100");
  
  switch (dateFilter) {
    case "Next48Hours":
      return {
         "start-date": toCustomISODate(now),
         "end-date": toCustomISODate(addDays(now, 2)), }
    case "Published":
      return {
        "start-date": toISODate(startOfDayDisplayed(pastDate)),
        "end-date": toCustomISODate(now),
      }
    case "All":
      return {
        "start-date": toISODate(startOfDayDisplayed(pastDate)),
        "end-date": toCustomISODate(addDays(now, 2)),
      }
  }
}

const transformFilter = ({ categories, segments, tags, dateFilter, sortOrder }: Filter) => ({
  ...(categories.length && { categories: categories.join(",") }),
  ...(segments.length && { segments: segments.join(",") }),
  ...(tags.length && { tags: tags.join(",") }),
  ...toDateRange(dateFilter),
  sort: sortOrder.toLowerCase(),
})

const get = async (url: string, { clientId, apiToken }: Credentials, extraParams = {}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const eventName = urlParams.get("event-name") || "milan-olympics-2026"
  const params = {
    ...extraParams,
    client_id: clientId,
    api_token: apiToken,
    "event-name": eventName,
  }

  let response
  try {
    response = await axios.get(url, { params })
  } catch (e) {
    response = e.response
  }

  switch (response && response.status) {
    case 200:
      return response.data
    case 401:
    case 403:
      throw new SystemError(ErrorCode.Authorization)
    default:
      throw new SystemError(ErrorCode.Api)
  }
}
